import { FONTS } from "./constants";

// Draws the design onto an offscreen 2D canvas so rotations/crops match the on-screen layers exactly
export async function renderDesignPreview(canvasEl, { layers, mode, material, stickerShape, plaqueShape }) {
  try {
    if (!canvasEl) return null;
    const rect = canvasEl.getBoundingClientRect();
    // Cap render resolution to keep the JPEG small enough for sessionStorage
    const targetW = Math.min(1400, Math.max(600, Math.round(rect.width * 2)));
    const w = targetW;
    const h = Math.round(w * (rect.height / rect.width));
    const scale = w / rect.width;
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext("2d");

    // --- Background ---
    ctx.save();
    if (mode === "sticker") {
      const shape = stickerShape || "rounded";
      const radius = shape === "circle" ? Math.min(w, h) / 2 : shape === "square" ? 4 * scale : shape === "diecut" ? 32 * scale : 16 * scale;
      ctx.fillStyle = "#ffffff";
      if (shape === "circle") {
        ctx.beginPath();
        ctx.arc(w / 2, h / 2, Math.min(w, h) / 2 - 8 * scale, 0, Math.PI * 2);
        ctx.fill();
      } else {
        const pad = 20 * scale;
        const bx = pad, by = pad, bw = w - pad * 2, bh = h - pad * 2;
        ctx.beginPath();
        ctx.moveTo(bx + radius, by);
        ctx.lineTo(bx + bw - radius, by);
        ctx.arcTo(bx + bw, by, bx + bw, by + radius, radius);
        ctx.lineTo(bx + bw, by + bh - radius);
        ctx.arcTo(bx + bw, by + bh, bx + bw - radius, by + bh, radius);
        ctx.lineTo(bx + radius, by + bh);
        ctx.arcTo(bx, by + bh, bx, by + bh - radius, radius);
        ctx.lineTo(bx, by + radius);
        ctx.arcTo(bx, by, bx + radius, by, radius);
        ctx.closePath();
        ctx.fill();
        ctx.setLineDash([8 * scale, 6 * scale]);
        ctx.lineWidth = 2 * scale;
        ctx.strokeStyle = "#c4c0b4";
        ctx.stroke();
        ctx.setLineDash([]);
      }
    } else {
      // Engraving mode: draw the material texture image
      const urlMatch = (material.bg || "").match(/url\(['"]?([^'")]+)['"]?\)/);
      const bgUrl = urlMatch ? urlMatch[1] : null;
      if (bgUrl) {
        const bgImg = await new Promise((resolve) => {
          const img = new Image();
          img.crossOrigin = "anonymous";
          img.onload = () => resolve(img);
          img.onerror = () => resolve(null);
          img.src = bgUrl;
        });
        if (bgImg) {
          const ir = bgImg.width / bgImg.height;
          const cr = w / h;
          let dw, dh;
          if (ir > cr) { dh = h; dw = h * ir; } else { dw = w; dh = w / ir; }
          if (plaqueShape === "circle") {
            ctx.beginPath();
            ctx.arc(w / 2, h / 2, Math.min(w, h) / 2, 0, Math.PI * 2);
            ctx.clip();
          }
          ctx.drawImage(bgImg, (w - dw) / 2, (h - dh) / 2, dw, dh);
        } else {
          ctx.fillStyle = "#5b3520"; ctx.fillRect(0, 0, w, h);
        }
      } else {
        ctx.fillStyle = "#5b3520"; ctx.fillRect(0, 0, w, h);
      }
    }
    ctx.restore();
    // Clip layers to the circular shape just like the on-screen overflow-hidden canvas
    if ((mode === "sticker" && stickerShape === "circle") || (mode !== "sticker" && plaqueShape === "circle")) {
      ctx.beginPath();
      ctx.arc(w / 2, h / 2, Math.min(w, h) / 2, 0, Math.PI * 2);
      ctx.clip();
    }

    // --- Pre-load images for image layers ---
    const imageAssets = {};
    await Promise.all(layers.filter((l) => l.type === "image").map((l) => new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => { imageAssets[l.id] = img; resolve(); };
      img.onerror = () => resolve();
      img.src = l.src;
    })));

    // --- Draw each layer in order ---
    for (const l of layers) {
      const cx = l.x * w;
      const cy = l.y * h;
      const rot = ((l.rotation || 0) * Math.PI) / 180;
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(rot);
      if (l.type === "text") {
        const font = FONTS.find((f) => f.key === l.font) || FONTS[0];
        const sizeCap = Math.min(l.size, Math.max(14, (rect.width) * 0.14));
        const fontSize = sizeCap * scale;
        ctx.font = `${font.italic ? "italic " : ""}${fontSize}px ${font.family}`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillStyle = l.color || "#F3E8CF";
        ctx.shadowColor = "rgba(0,0,0,0.35)";
        ctx.shadowBlur = 8 * scale;
        ctx.shadowOffsetY = 2 * scale;
        ctx.fillText(l.text || "", 0, 0);
      } else if (l.type === "image") {
        const img = imageAssets[l.id];
        if (img) {
          const dw = (l.width || 220) * scale;
          const dh = (l.height || 220) * scale;
          ctx.globalAlpha = l.opacity ?? 1;
          // Match CSS object-fit: cover — crop the source instead of stretching it
          const ir = img.naturalWidth / img.naturalHeight;
          const br = dw / dh;
          let sw = img.naturalWidth, sh = img.naturalHeight;
          if (ir > br) sw = sh * br; else sh = sw / br;
          const sx = (img.naturalWidth - sw) / 2, sy = (img.naturalHeight - sh) / 2;
          if (l.engraved) {
            const off = document.createElement("canvas");
            off.width = Math.max(1, Math.round(dw));
            off.height = Math.max(1, Math.round(dh));
            const octx = off.getContext("2d");
            octx.filter = `grayscale(1) contrast(${l.contrast ?? 1.6}) brightness(${l.brightness ?? 0.9})`;
            octx.drawImage(img, sx, sy, sw, sh, 0, 0, off.width, off.height);
            ctx.drawImage(off, -dw / 2, -dh / 2, dw, dh);
          } else {
            ctx.drawImage(img, sx, sy, sw, sh, -dw / 2, -dh / 2, dw, dh);
          }
        }
      }
      ctx.restore();
    }
    // JPEG at 0.82 quality — way smaller than PNG, no visible loss for engraving previews
    return canvas.toDataURL("image/jpeg", 0.82);
  } catch (e) {
    console.warn("Preview render failed", e);
    return null;
  }
}
